import React, { useState, useEffect } from 'react';
import {
  DetailedProgressData,
  ProgressionDataPoint,
  CriterionAverage,
} from '../types/models';
import { AssessmentReport } from './AssessmentReport';
import '../styles/ProgressDashboard.css';

interface ProgressDashboardProps {
  studentProfileId: string;
  studentName: string;
  yearLevel?: number;
  onBack?: () => void;
}

/**
 * Component for displaying student progress over time.
 * Shows score progression, average scores by criterion, and progress trends.
 * Implements Requirements 5.4, 5.5
 */
export const ProgressDashboard: React.FC<ProgressDashboardProps> = ({
  studentProfileId,
  studentName,
  yearLevel = 5,
  onBack,
}) => {
  const [progressData, setProgressData] = useState<DetailedProgressData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [activeView, setActiveView] = useState<'overview' | 'criteria' | 'history'>('overview');
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string | null>(null);

  useEffect(() => {
    fetchProgressData();
  }, [studentProfileId]);

  const fetchProgressData = async () => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const storedTokens = localStorage.getItem('authTokens');
      const token = storedTokens ? JSON.parse(storedTokens).idToken : null;
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${apiUrl}/api/progress/${studentProfileId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load progress data');
      }

      setProgressData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend: string): string => {
    switch (trend) {
      case 'improving':
        return '📈';
      case 'declining':
        return '📉';
      default:
        return '➡️';
    }
  };

  const getTrendClass = (trend: string): string => {
    switch (trend) {
      case 'improving':
        return 'trend--improving';
      case 'declining':
        return 'trend--declining';
      default:
        return 'trend--stable';
    }
  };

  const getScoreColor = (score: number): string => {
    const numScore = Number(score) || 0;
    if (numScore >= 8) return '#4caf50';
    if (numScore >= 6) return '#8bc34a';
    if (numScore >= 4) return '#ffc107';
    if (numScore >= 2) return '#ff9800';
    return '#f44336';
  };

  const renderScoreBar = (score: number, maxScore: number = 10) => {
    const numScore = Number(score) || 0;
    const percentage = (numScore / maxScore) * 100;
    return (
      <div className="progress-score-bar">
        <div
          className="progress-score-bar__fill"
          style={{
            width: `${percentage}%`,
            backgroundColor: getScoreColor(numScore),
          }}
        />
        <span className="progress-score-bar__value">{numScore.toFixed(1)}</span>
      </div>
    );
  };

  const renderOverviewTab = () => {
    if (!progressData) return null;

    const { statistics, trend } = progressData;

    return (
      <div className="progress-tab progress-tab--overview">
        {/* Summary Cards */}
        <div className="progress-summary-cards">
          <div className="summary-card">
            <span className="summary-card__icon">📊</span>
            <div className="summary-card__content">
              <span className="summary-card__value">{statistics?.totalAssessments || 0}</span>
              <span className="summary-card__label">Total Assessments</span>
            </div>
          </div>

          <div className="summary-card">
            <span className="summary-card__icon">⭐</span>
            <div className="summary-card__content">
              <span className="summary-card__value">{statistics?.averageScore != null ? Number(statistics.averageScore).toFixed(1) : '0.0'}</span>
              <span className="summary-card__label">Average Score</span>
            </div>
          </div>

          <div className={`summary-card ${getTrendClass(trend || 'stable')}`}>
            <span className="summary-card__icon">{getTrendIcon(trend || 'stable')}</span>
            <div className="summary-card__content">
              <span className="summary-card__value">{trend ? trend.charAt(0).toUpperCase() + trend.slice(1) : 'Stable'}</span>
              <span className="summary-card__label">Progress Trend</span>
            </div>
          </div>

          {statistics?.improvement != null && (
            <div className={`summary-card ${Number(statistics.improvement) >= 0 ? 'trend--improving' : 'trend--declining'}`}>
              <span className="summary-card__icon">{Number(statistics.improvement) >= 0 ? '⬆️' : '⬇️'}</span>
              <div className="summary-card__content">
                <span className="summary-card__value">
                  {Number(statistics.improvement) >= 0 ? '+' : ''}{Number(statistics.improvement).toFixed(1)}
                </span>
                <span className="summary-card__label">Score Change</span>
              </div>
            </div>
          )}
        </div>

        {/* Best & Worst Areas */}
        {statistics && (statistics.bestCriterion || statistics.worstCriterion) && (
          <div className="progress-highlights">
            {statistics.bestCriterion && (
              <div className="highlight-card highlight-card--best">
                <h4 className="highlight-card__title">
                  <span className="highlight-icon">🏆</span> Strongest Area
                </h4>
                <p className="highlight-card__criterion">{statistics.bestCriterion.displayName}</p>
                <p className="highlight-card__score">Average: {Number(statistics.bestCriterion.average).toFixed(1)}/10</p>
              </div>
            )}
            {statistics.worstCriterion && (
              <div className="highlight-card highlight-card--focus">
                <h4 className="highlight-card__title">
                  <span className="highlight-icon">🎯</span> Focus Area
                </h4>
                <p className="highlight-card__criterion">{statistics.worstCriterion.displayName}</p>
                <p className="highlight-card__score">Average: {Number(statistics.worstCriterion.average).toFixed(1)}/10</p>
              </div>
            )}
          </div>
        )}

        {/* Score Progression Chart */}
        {progressData.progression && progressData.progression.length > 0 && (
          <div className="progress-section">
            <h3 className="progress-section__title">Score Progression</h3>
            <div className="progression-chart">
              {progressData.progression.map((point: ProgressionDataPoint, index: number) => {
                const totalScore = Number(point.totalScore) || 0;
                return (
                  <div key={point.assessmentId} className="progression-point">
                    <div
                      className="progression-bar"
                      style={{
                        height: `${(totalScore / 10) * 100}%`,
                        backgroundColor: getScoreColor(totalScore),
                      }}
                      title={`Assessment ${index + 1}: ${totalScore.toFixed(1)}/10`}
                    />
                    <span className="progression-label">{index + 1}</span>
                  </div>
                );
              })}
            </div>
            <p className="progression-caption">Assessment number (oldest to newest)</p>
          </div>
        )}
      </div>
    );
  };


  const renderCriteriaTab = () => {
    if (!progressData || !progressData.averageScores) return null;

    const criteria = Object.entries(progressData.averageScores)
      .filter(([key]) => key !== 'overall')
      .map(([criterion, data]) => ({
        criterion,
        ...(data as CriterionAverage),
      }));

    // Sort by average score descending
    criteria.sort((a, b) => Number(b.average) - Number(a.average));

    return (
      <div className="progress-tab progress-tab--criteria">
        <h3 className="progress-section__title">Average Scores by Criterion</h3>
        <div className="criteria-list">
          {criteria.map((item) => (
            <div key={item.criterion} className="criterion-card">
              <div className="criterion-card__header">
                <span className="criterion-card__name">{item.displayName}</span>
                <span className="criterion-card__count">({item.count} assessments)</span>
              </div>
              {renderScoreBar(Number(item.average))}
            </div>
          ))}
        </div>

        {/* Overall Average */}
        {progressData.averageScores.overall && (
          <div className="overall-average">
            <h4 className="overall-average__title">Overall Average</h4>
            <div className="overall-average__score">
              <span className="overall-average__value">
                {Number((progressData.averageScores.overall as CriterionAverage).average).toFixed(1)}
              </span>
              <span className="overall-average__max">/10</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderHistoryTab = () => {
    if (!progressData || !progressData.assessments) return null;

    // Sort assessments by date (newest first)
    const sortedAssessments = [...progressData.assessments].sort(
      (a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()
    );

    return (
      <div className="progress-tab progress-tab--history">
        <h3 className="progress-section__title">Assessment History</h3>
        <p className="progress-section__subtitle">Click on an assessment to view detailed feedback</p>
        {sortedAssessments.length === 0 ? (
          <p className="no-history">No assessments yet.</p>
        ) : (
          <div className="history-list">
            {sortedAssessments.map((assessment, index) => {
              const scores = assessment.scores || {};
              const scoreValues = Object.values(scores).map(v => Number(v));
              const avgScore = scoreValues.length > 0
                ? scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length
                : 0;

              return (
                <div 
                  key={assessment.assessmentId} 
                  className="history-card history-card--clickable"
                  onClick={() => setSelectedAssessmentId(assessment.assessmentId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSelectedAssessmentId(assessment.assessmentId);
                    }
                  }}
                >
                  <div className="history-card__header">
                    <span className="history-card__number">#{sortedAssessments.length - index}</span>
                    <span className="history-card__date">
                      {new Date(assessment.generatedAt).toLocaleDateString()}
                    </span>
                    <span className="history-card__format">{assessment.testFormat?.toUpperCase()}</span>
                  </div>
                  <div className="history-card__score">
                    <span
                      className="history-card__score-value"
                      style={{ color: getScoreColor(avgScore) }}
                    >
                      {avgScore.toFixed(1)}
                    </span>
                    <span className="history-card__score-max">/10</span>
                  </div>
                  <div className="history-card__criteria">
                    {Object.entries(scores).map(([criterion, score]) => (
                      <div key={criterion} className="history-criterion">
                        <span className="history-criterion__name">
                          {criterion.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <span
                          className="history-criterion__score"
                          style={{ color: getScoreColor(score as number) }}
                        >
                          {score}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="history-card__action">
                    <span className="view-details-link">View Details →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="progress-dashboard progress-dashboard--loading">
        <div className="loading-spinner" />
        <p>Loading progress data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="progress-dashboard progress-dashboard--error">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchProgressData} className="btn-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!progressData || progressData.totalAssessments === 0) {
    return (
      <div className="progress-dashboard progress-dashboard--empty">
        <div className="empty-state">
          <span className="empty-icon">📝</span>
          <h3>No Progress Data Yet</h3>
          <p>Complete some writing assessments to start tracking progress.</p>
          {onBack && (
            <button onClick={onBack} className="btn-primary">
              Start Writing
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="progress-dashboard">
      {/* Header */}
      <div className="progress-header">
        <div className="progress-header__left">
          {onBack && (
            <button onClick={onBack} className="btn-back" aria-label="Go back">
              ← Back
            </button>
          )}
          <div className="progress-header__info">
            <h2 className="progress-header__title">Progress Dashboard</h2>
            <p className="progress-header__student">{studentName}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="progress-tabs">
        <button
          className={`progress-tab-btn ${activeView === 'overview' ? 'progress-tab-btn--active' : ''}`}
          onClick={() => setActiveView('overview')}
        >
          Overview
        </button>
        <button
          className={`progress-tab-btn ${activeView === 'criteria' ? 'progress-tab-btn--active' : ''}`}
          onClick={() => setActiveView('criteria')}
        >
          By Criterion
        </button>
        <button
          className={`progress-tab-btn ${activeView === 'history' ? 'progress-tab-btn--active' : ''}`}
          onClick={() => setActiveView('history')}
        >
          History
        </button>
      </div>

      {/* Tab Content */}
      <div className="progress-content">
        {activeView === 'overview' && renderOverviewTab()}
        {activeView === 'criteria' && renderCriteriaTab()}
        {activeView === 'history' && renderHistoryTab()}
      </div>

      {/* Assessment Detail Modal */}
      {selectedAssessmentId && (
        <div className="assessment-modal-overlay" onClick={() => setSelectedAssessmentId(null)}>
          <div className="assessment-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="assessment-modal-close" 
              onClick={() => setSelectedAssessmentId(null)}
              aria-label="Close"
            >
              ×
            </button>
            <AssessmentReport
              assessmentId={selectedAssessmentId}
              studentProfileId={studentProfileId}
              yearLevel={yearLevel}
              onBack={() => setSelectedAssessmentId(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressDashboard;
